import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './DashboardPage.css';
import { safeDate } from '../utils/dateUtils';

/* ── Icon set — thin-line SVGs, matching the sidebar's existing style
   (24x24 viewBox, stroke-based) rather than emoji ── */
const Icon = ({ name, size = 18 }) => {
    const paths = {
        crown: <path d="M3 18h18l-1.5-9-4.5 4-3-6-3 6-4.5-4z" />,
        creditCard: <><rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" /></>,
        star: <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />,
        target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></>,
        clock: <><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 16 14" /></>,
        checkCircle: <><circle cx="12" cy="12" r="9" /><polyline points="8 12 11 15 16 9" /></>,
        rocket: <><path d="M12 2c2.5 2 4 5.5 4 9 0 2-.5 4-1.5 6l-2.5 3-2.5-3C8.5 15 8 13 8 11c0-3.5 1.5-7 4-9z" /><circle cx="12" cy="10" r="1.5" /><path d="M8.5 16 6 20l3-1" /><path d="M15.5 16 18 20l-3-1" /></>,
        wallet: <><path d="M20 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" /><path d="M16 7V5a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v2" /><circle cx="17" cy="13" r="1.5" /></>,
        document: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></>,
        idCard: <><rect x="2" y="4" width="20" height="16" rx="2" /><circle cx="8" cy="11" r="2" /><path d="M5 17c.5-1.8 1.8-3 3-3s2.5 1.2 3 3" /><line x1="14" y1="9" x2="18" y2="9" /><line x1="14" y1="13" x2="18" y2="13" /></>,
        chat: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
        clipboard: <><rect x="7" y="3" width="10" height="4" rx="1" /><path d="M9 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-3" /></>,
        info: <><circle cx="12" cy="12" r="9" /><line x1="12" y1="11" x2="12" y2="16" /><line x1="12" y1="8" x2="12.01" y2="8" /></>,
        chevronRight: <polyline points="9 18 15 12 9 6" />,
        alertTriangle: <><path d="M12 3 2 20h20z" /><line x1="12" y1="9" x2="12" y2="14" /><line x1="12" y1="17" x2="12.01" y2="17" /></>,
    };
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {paths[name]}
        </svg>
    );
};

function DashboardPage() {
    const navigate = useNavigate();

    const [user, setUser] = useState({ name: 'Loading...', kycStatus: 'pending' });
    const [stats, setStats] = useState({
        creditScore: null, availableCredit: '5,00,000',
        activeLoans: 0, pendingLoans: 0, upcomingPayment: '0'
    });
    const [gamification, setGamification] = useState({
        pointsBalance: 0, totalXp: 0, currentLevel: 1, currentStage: 1
    });
    const [activities, setActivities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const API_URL = process.env.REACT_APP_API_URL || '';

    const fetchDashboardData = useCallback(async () => {
        const session = JSON.parse(localStorage.getItem('userSession') || 'null');
        if (!session?.userId) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/dashboard/all-data/${session.userId}`, {
                headers: session.token ? { Authorization: `Bearer ${session.token}` } : undefined
            });
            const data = await res.json();
            if (data.success) {
                setUser({ id: session.userId, name: data.user.name, kycStatus: data.user.kycStatus || 'pending' });
                setStats(data.stats);
                if (data.gamification) setGamification(data.gamification);
                setActivities(data.activities || []);
            }
        } catch (_) {
        } finally {
            setIsLoading(false);
        }
    }, [API_URL]);

    // If no credit score yet, pull the bureau report directly (synchronous on the server)
    // and drop the returned score straight into the gauge. Runs at most once per mount.
    const ensureCreditScore = useCallback(async () => {
        const session = JSON.parse(localStorage.getItem('userSession') || 'null');
        if (!session?.userId) return;
        try {
            const res = await fetch(`${API_URL}/api/bureau/consent-status`, {
                headers: session.token ? { Authorization: `Bearer ${session.token}` } : undefined
            });
            const data = await res.json();
            if (data?.success && data.creditScore != null) {
                setStats(prev => ({ ...prev, creditScore: Number(data.creditScore) }));
            }
        } catch (_) { /* leave the gauge in "Checking" */ }
    }, [API_URL]);

    useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

    useEffect(() => {
        if (!isLoading && stats.creditScore == null) {
            ensureCreditScore();
        }
    }, [isLoading, stats.creditScore, ensureCreditScore]);

    const displayName = (user && user.name && user.name !== 'Loading...')
        ? String(user.name)
            .split(' ')
            .filter(Boolean)
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ')
        : 'User';

    const getScoreInfo = (score) => {
        if (score === null || score === undefined || !Number.isFinite(Number(score))) return { label: 'Checking', color: '#94a3b8', track: '#e2e8f0' };
        if (score >= 750) return { label: 'Excellent', color: '#2F6F4E', track: '#d1fae5' };
        if (score >= 650) return { label: 'Good', color: '#A5761E', track: '#fef3c7' };
        return { label: 'Fair', color: '#A23B2E', track: '#fee2e2' };
    };
    const scoreInfo = getScoreInfo(stats.creditScore);
    const gaugeR = 54;
    const gaugeC = 2 * Math.PI * gaugeR;
    const gaugeArc = gaugeC * 0.75;
    const gaugeFill = (Math.min(100, ((stats.creditScore || 0) / 900) * 100) / 100) * gaugeArc;

    const getGreeting = () => {
        const h = new Date().getHours();
        if (h < 12) return 'Good morning';
        if (h < 17) return 'Good afternoon';
        return 'Good evening';
    };

    const timeAgo = (dateStr) => {
        const parsed = safeDate(dateStr);
        const diff = parsed ? Math.floor((Date.now() - parsed.getTime()) / 1000) : 0;
        if (diff < 60) return 'Just now';
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    };

    const getActivityIcon = (type) => {
        const map = { payment: 'creditCard', document: 'document', credit: 'star', login: 'idCard', loan: 'wallet' };
        return map[(type || '').toLowerCase()] || 'info';
    };

    const quickActions = [
        { icon: 'rocket',     label: 'Apply for Loan',  desc: 'Start your journey',       path: '/dashboard/apply-loan', highlight: true },
        { icon: 'wallet',     label: 'My Loans',         desc: 'View all loans',           path: '/dashboard/loans' },
        { icon: 'creditCard', label: 'Payments',         desc: 'Pay EMI & dues',           path: '/dashboard/payments' },
        { icon: 'document',   label: 'Documents',        desc: 'Upload & manage',          path: '/dashboard/documents' },
        { icon: 'idCard',     label: 'KYC Verify',       desc: 'Complete identity',        path: '/dashboard/kyc' },
        { icon: 'chat',       label: 'Support',          desc: 'Get help',                 path: '/dashboard/support' },
    ];

    const normalizedKyc = String(user.kycStatus || '').toLowerCase();
    const hasKycCompleted = normalizedKyc === 'completed';
    const hasLoanApplied = Number(stats.activeLoans || 0) > 0 || Number(stats.pendingLoans || 0) > 0;
    const hasRepaymentActivity = (activities || []).some((a) => {
        const type = String(a?.activity_type || '').toLowerCase();
        const desc = String(a?.description || '').toLowerCase();
        return type.includes('payment') || desc.includes('payment') || desc.includes('emi');
    });

    const journeySteps = [
        { step: 1, label: 'Sign Up',          pts: 100, done: Boolean(user.id) },
        { step: 2, label: 'KYC Verified',     pts: 500, done: hasKycCompleted },
        { step: 3, label: 'Loan Applied',     pts: 200, done: hasLoanApplied },
        { step: 4, label: 'First Repayment',  pts: 300, done: hasRepaymentActivity },
    ];
    const earnedPts = journeySteps.filter(s => s.done).reduce((a, s) => a + s.pts, 0);
    const totalPts = journeySteps.reduce((a, s) => a + s.pts, 0);

    if (isLoading) return (
        <div className="kdash">
            <div className="kdash-loading">
                <div className="kdash-spinner" />
                <span>Loading your dashboard…</span>
            </div>
        </div>
    );

    return (
        <div className="kdash">
            {/* ── Welcome Hero ── */}
            <section className="kdash-hero">
                <div className="kdash-hero-content">
                    <div className="kdash-hero-top-row">
                        <p className="kdash-hero-greeting">{getGreeting()},</p>
                        <div className="kdash-hero-level-badge">
                            <div className="level-badge-icon"><Icon name="crown" size={14} /></div>
                            <div className="level-badge-info">
                                <span className="level-badge-title">Level {gamification.currentLevel}</span>
                                <span className="level-badge-xp">{gamification.totalXp} XP</span>
                            </div>
                        </div>
                    </div>
                    <h1 className="kdash-hero-name">{displayName}</h1>
                    <p className="kdash-hero-sub">Welcome back to your dashboard.</p>
                </div>

                {/* Credit Score Gauge */}
                <div className="kdash-hero-gauge">
                    <svg viewBox="0 0 130 130" className="kdash-gauge-svg">
                        <circle
                            cx="65"
                            cy="65"
                            r={gaugeR}
                            fill="none"
                            stroke="#e9d9ae"
                            strokeWidth="10"
                            strokeDasharray={`${gaugeArc} ${gaugeC}`}
                            strokeLinecap="round"
                            transform="rotate(135 65 65)"
                        />
                        <circle
                            cx="65"
                            cy="65"
                            r={gaugeR}
                            fill="none"
                            stroke={scoreInfo.color}
                            strokeWidth="10"
                            strokeDasharray={`${gaugeFill} ${gaugeC}`}
                            strokeLinecap="round"
                            transform="rotate(135 65 65)"
                            className="kdash-gauge-fill"
                        />
                    </svg>
                    <div className="kdash-gauge-center">
                        <span className="kdash-gauge-value">{(stats.creditScore !== null && stats.creditScore !== undefined) ? stats.creditScore : '--'}</span>
                        <span className="kdash-gauge-label" style={{ color: scoreInfo.color }}>{scoreInfo.label}</span>
                    </div>
                    <p className="kdash-gauge-caption">Credit Score</p>
                </div>
            </section>

            {/* ── Stat Cards ── */}
            <section className="kdash-stats">
                <div className="kdash-stat-card">
                    <div className="kdash-stat-header">
                        <div className="kdash-stat-icon gold"><Icon name="creditCard" size={19} /></div>
                        {Number(String(stats.upcomingPayment).replace(/,/g, '')) > 0
                            ? <span className="kdash-stat-chip red">Due soon</span>
                            : <span className="kdash-stat-chip neutral">No dues</span>}
                    </div>
                    <div className="kdash-stat-body">
                        <span className="kdash-stat-label">Next Payment</span>
                        <span className="kdash-stat-value">₹{stats.upcomingPayment}</span>
                    </div>
                </div>

                {/* Gamification Points Card */}
                <div className="kdash-stat-card points-card" onClick={() => navigate('/dashboard/apply-loan')} style={{ cursor: 'pointer' }}>
                    <div className="kdash-stat-header">
                        <div className="kdash-stat-icon gold"><Icon name="star" size={19} /></div>
                        <span className="kdash-stat-chip amber">Redeemable</span>
                    </div>
                    <div className="kdash-stat-body">
                        <span className="kdash-stat-label">Reward Points</span>
                        <span className="kdash-stat-value">{gamification.pointsBalance.toLocaleString()}</span>
                    </div>
                </div>
            </section>

            {/* ── Journey Progress + Quick Actions ── */}
            <section className="kdash-grid kdash-grid-3">

                {/* Apply Loan CTA */}
                <div className="kdash-panel kdash-apply-cta">
                    {stats.pendingLoans > 0 ? (
                        <>
                            <div className="apply-cta-badge apply-cta-badge-pending"><Icon name="clock" size={13} /> Under Review</div>
                            <h2>Loan Pending Approval</h2>
                            <p>Your loan application is currently under review by our underwriting team. We will notify you once it is approved.</p>
                            <button className="apply-cta-btn apply-cta-btn-pending" onClick={() => navigate('/dashboard/loans')}>
                                View Status →
                            </button>
                        </>
                    ) : stats.activeLoans > 0 ? (
                        <>
                            <div className="apply-cta-badge apply-cta-badge-active"><Icon name="checkCircle" size={13} /> Active Loan</div>
                            <h2>You have an active loan</h2>
                            <p>You currently have an ongoing loan. You can apply for a new one after completing your repayments.</p>
                            <button className="apply-cta-btn apply-cta-btn-active" onClick={() => navigate('/dashboard/loans')}>
                                View My Loan →
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="apply-cta-badge"><Icon name="target" size={13} /> Start Here</div>
                            <h2>Ready to Apply?</h2>
                            <p>Get pre-approved in minutes. Follow our step-by-step journey and earn reward points along the way.</p>
                            <div className="apply-cta-points">
                                <span>Earn up to</span>
                                <strong>₹1,150 points</strong>
                                <span>redeemable on repayment</span>
                            </div>
                            <button className="apply-cta-btn" onClick={() => navigate('/dashboard/apply-loan')}>
                                {hasKycCompleted || hasLoanApplied ? 'Continue Loan Journey →' : 'Apply for Loan →'}
                            </button>
                        </>
                    )}
                </div>

                {/* Journey Steps */}
                <div className="kdash-panel">
                    <div className="kdash-panel-header">
                        <h2>Your Journey</h2>
                        <span className="kdash-pts-badge">{earnedPts}/{totalPts} pts</span>
                    </div>
                    <div className="kdash-journey-steps">
                        {journeySteps.map((s, i) => (
                            <div key={s.step} className={`kdash-journey-step ${s.done ? 'done' : ''}`}>
                                <div className="step-indicator">
                                    <div className="step-dot">{s.done ? <Icon name="checkCircle" size={13} /> : s.step}</div>
                                    {i < journeySteps.length - 1 && <div className="step-line" />}
                                </div>
                                <div className="step-info">
                                    <span className="step-label">{s.label}</span>
                                    <span className="step-pts">+{s.pts} pts</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="kdash-panel">
                    <div className="kdash-panel-header"><h2>Quick Actions</h2></div>
                    <div className="kdash-actions">
                        {quickActions.map(a => (
                            <button key={a.path} className={`kdash-action-card ${a.highlight ? 'highlight' : ''}`}
                                onClick={() => navigate(a.path)}>
                                <span className="kdash-action-icon"><Icon name={a.icon} size={20} /></span>
                                <span className="kdash-action-label">{a.label}</span>
                                <span className="kdash-action-desc">{a.desc}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Recent Activity ── */}
            <section className="kdash-panel" style={{ marginTop: '1rem' }}>
                <div className="kdash-panel-header">
                    <h2>Recent Activity</h2>
                    {activities.length > 0 && (
                        <button className="kdash-view-all" onClick={() => navigate('/dashboard/loans')}>View all →</button>
                    )}
                </div>
                <div className="kdash-activity-list">
                    {activities.length > 0 ? activities.slice(0, 5).map(act => (
                        <div key={act.id} className="kdash-activity-item">
                            <div className="kdash-activity-left">
                                <div className="kdash-activity-icon"><Icon name={getActivityIcon(act.activity_type)} size={16} /></div>
                                <div className="kdash-activity-info">
                                    <h4>{act.activity_type}</h4>
                                    <p>{act.description}</p>
                                </div>
                            </div>
                            <span className="kdash-activity-time">{timeAgo(act.created_at)}</span>
                        </div>
                    )) : (
                        <div className="kdash-activity-empty">
                            <span className="kdash-empty-icon"><Icon name="clipboard" size={26} /></span>
                            <p>No recent activity yet.</p>
                            <span>Your transactions, payments, and updates will appear here.</span>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}

export default DashboardPage;
